import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { LedgerSummary, Company } from '../types/billing';
import LedgerPDF from '../services/ledgerPDFGenerator';
import { emailService } from '../services/emailService';

// Company information for header
const COMPANY_INFO = {
  name: "AVI'S ENGINEERING & CONSULTANCY",
  subtitle: 'NDT SERVICES',
  address: 'SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Savedi, Ahilyanagar',
  phone: '7887985350',
  email: 'avis.engineering2025@gmail.com',
  gstn: '27FLTPM3802F1ZU',
};

interface LedgerTableProps {
  summary: LedgerSummary;
  company?: Company;
}

const LedgerTable: React.FC<LedgerTableProps> = ({ summary, company }) => {
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '';
    const rounded = Math.round(value);
    return new Intl.NumberFormat('en-IN', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(rounded);
  };

  const totalDebit = summary.accounts.reduce((acc, a) => acc + (a.debitBalance || 0), 0);
  const totalCredit = summary.accounts.reduce((acc, a) => acc + (a.creditBalance || 0), 0);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const companyName = company ? company.name : 'All Companies';
    const totalBilled = summary.accounts.find(a => a.ledgerName.includes('Sales'))?.creditBalance || 0;
    const totalReceived = summary.accounts.find(a => a.ledgerName.includes('Cash/Bank'))?.debitBalance || 0;
    const outstanding = summary.accounts.find(a => a.ledgerName.includes('Outstanding'))?.debitBalance || 0;

    // Generate transaction rows
    const transactionRows = summary.transactions && summary.transactions.length > 0
      ? summary.transactions.map(trans => {
          const formattedDate = trans.date 
            ? new Date(trans.date).toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: 'short', 
                year: '2-digit' 
              }).replace(/\s/g, '-')
            : '';
          
          const isBold = trans.isClosingBalance && trans.debit === trans.credit;
          const borderStyle = trans.isSubtotal || (trans.isClosingBalance && trans.debit === trans.credit) 
            ? 'border-top: 2px solid #333;' 
            : 'border-bottom: 1px solid #ddd;';
          const boldStyle = isBold ? 'font-weight: bold;' : '';
          
          return `
            <tr>
              <td style="padding: 8px; ${borderStyle}">${formattedDate}</td>
              <td style="padding: 8px; ${borderStyle} ${boldStyle}">${trans.particulars}</td>
              <td style="padding: 8px; ${borderStyle}">${trans.vchType || ''}</td>
              <td style="padding: 8px; ${borderStyle}">${trans.vchNo || ''}</td>
              <td style="padding: 8px; ${borderStyle} text-align: right; ${boldStyle}">${trans.debit !== undefined ? formatCurrency(trans.debit) : ''}</td>
              <td style="padding: 8px; ${borderStyle} text-align: right; ${boldStyle}">${trans.credit !== undefined ? formatCurrency(trans.credit) : ''}</td>
            </tr>
          `;
        }).join('')
      : '';

    const ledgerRows = summary.accounts.map(acc => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${acc.ledgerName}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${formatCurrency(acc.debitBalance)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${formatCurrency(acc.creditBalance)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ledger Balance - ${companyName}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 12px;
            line-height: 1.4;
          }
          .company-header {
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #2C4A6B;
          }
          .company-info {
            margin-bottom: 15px;
          }
          .company-name {
            font-size: 16px;
            font-weight: bold;
            color: #2C4A6B;
            margin-bottom: 3px;
          }
          .company-subtitle {
            font-size: 12px;
            font-weight: bold;
            color: #2C4A6B;
            margin-bottom: 8px;
          }
          .company-details {
            font-size: 10px;
            color: #333333;
            line-height: 1.5;
          }
          .ledger-header {
            margin-bottom: 20px;
            padding-top: 10px;
          }
          .title {
            font-size: 20px;
            font-weight: bold;
            color: #2C4A6B;
            margin-bottom: 5px;
          }
          .subtitle {
            font-size: 12px;
            color: #666;
          }
          .client-company-name {
            font-size: 14px;
            font-weight: bold;
            margin-top: 10px;
            color: #333;
          }
          .summary {
            margin-bottom: 20px;
            padding: 15px;
            background-color: #F5F5F5;
            border-radius: 5px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-top: 10px;
          }
          .summary-item {
            background: white;
            padding: 10px;
            border-radius: 3px;
          }
          .summary-label {
            font-size: 9px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-value {
            font-size: 16px;
            font-weight: bold;
          }
          .total-billed { color: #10B981; }
          .total-received { color: #3B82F6; }
          .outstanding { color: #F59E0B; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th {
            background-color: #2C4A6B;
            color: white;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }
          th:last-child, td:last-child {
            text-align: right;
          }
          th:nth-child(2), td:nth-child(2) {
            text-align: right;
          }
          tfoot {
            background-color: #F5F5F5;
            font-weight: bold;
            border-top: 2px solid #2C4A6B;
          }
          .footer {
            margin-top: 30px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            font-size: 8px;
            color: #666;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <!-- Company Header -->
        <div class="company-header">
          <div class="company-info">
            <div class="company-name">${COMPANY_INFO.name}</div>
            <div class="company-subtitle">${COMPANY_INFO.subtitle}</div>
            <div class="company-details">
              <div>${COMPANY_INFO.address}</div>
              <div>Ph: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}</div>
              <div>GSTIN: ${COMPANY_INFO.gstn}</div>
            </div>
          </div>
        </div>

        <!-- Ledger Header -->
        <div class="ledger-header">
          <div class="title">Ledger Balance</div>
          <div class="subtitle">${summary.periodLabel}</div>
          ${company ? `<div class="client-company-name">Client: ${companyName}</div>` : ''}
        </div>

        ${summary.accounts.length > 0 ? `
        <div class="summary">
          <div style="font-size: 14px; font-weight: bold; color: #2C4A6B; margin-bottom: 10px;">Financial Summary</div>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Total Billed</div>
              <div class="summary-value total-billed">${formatCurrency(totalBilled)}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Received</div>
              <div class="summary-value total-received">${formatCurrency(totalReceived)}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Outstanding</div>
              <div class="summary-value outstanding">${formatCurrency(outstanding)}</div>
            </div>
          </div>
        </div>
        ` : ''}

        ${transactionRows ? `
        <div style="margin-top: 20px; margin-bottom: 20px;">
          <div style="font-size: 14px; font-weight: bold; color: #2C4A6B; margin-bottom: 10px;">Transaction Details</div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Vch Type</th>
                <th>Vch No.</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              ${transactionRows}
            </tbody>
          </table>
        </div>
        ` : ''}

        <table>
          <thead>
            <tr>
              <th>Ledger Name</th>
              <th>Debit Balance</th>
              <th>Credit Balance</th>
            </tr>
          </thead>
          <tbody>
            ${summary.accounts.length === 0 ? `
            <tr>
              <td colspan="3" style="padding: 8px; text-align: center;">No data available</td>
            </tr>
            ` : ledgerRows}
          </tbody>
          ${summary.accounts.length > 0 ? `
          <tfoot>
            <tr>
              <td style="padding: 8px; text-align: right; font-weight: bold;">Totals</td>
              <td style="padding: 8px; text-align: right; font-weight: bold;">${formatCurrency(totalDebit)}</td>
              <td style="padding: 8px; text-align: right; font-weight: bold;">${formatCurrency(totalCredit)}</td>
            </tr>
          </tfoot>
          ` : ''}
        </table>

        <div class="footer">
          Generated on ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadPDF = async () => {
    if (!company) {
      alert('Please select a company to download the ledger PDF.');
      return;
    }

    setIsDownloadingPDF(true);
    try {
      const blob = await pdf(<LedgerPDF summary={summary} company={company} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ledger_${company.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleSendEmail = async () => {
    if (!company) {
      alert('Please select a company to send the ledger balance.');
      return;
    }

    if (!company.email) {
      alert('Company email address is not available. Please update the company details.');
      return;
    }

    setIsSendingEmail(true);
    try {
      const success = await emailService.sendLedgerBalance(summary, company);
      if (success) {
        alert('Ledger balance sent successfully to ' + company.email);
      } else {
        alert('Failed to send ledger balance. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send ledger balance. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Ledger Balance</h3>
            <p className="text-sm text-gray-600">{summary.periodLabel}</p>
            {summary.accounts.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">No data available. Create some bills and record payments to see ledger data.</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
              title="Print Ledger"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloadingPDF || !company}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              title={company ? "Download PDF" : "Please select a company to download PDF"}
            >
              {isDownloadingPDF ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !company}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              title={company ? "Send via Email" : "Please select a company to send email"}
            >
              {isSendingEmail ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Financial Summary Cards */}
      {summary.accounts.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Billed</h4>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.accounts.find(a => a.ledgerName.includes('Sales'))?.creditBalance || 0)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Received</h4>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(summary.accounts.find(a => a.ledgerName.includes('Cash/Bank'))?.debitBalance || 0)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Outstanding</h4>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(summary.accounts.find(a => a.ledgerName.includes('Outstanding'))?.debitBalance || 0)}
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        {/* Detailed Transactions Table */}
        {summary.transactions && summary.transactions.length > 0 && (
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Transaction Details</h4>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Particulars</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vch Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vch No.</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summary.transactions.map((trans, idx) => {
                  const formattedDate = trans.date 
                    ? new Date(trans.date).toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: '2-digit' 
                      }).replace(/\s/g, '-')
                    : '';
                  
                  const isBold = trans.isClosingBalance && trans.debit === trans.credit;
                  const hasBorder = trans.isSubtotal || (trans.isClosingBalance && trans.debit === trans.credit);
                  
                  return (
                    <tr 
                      key={idx} 
                      className={`hover:bg-gray-50 ${hasBorder ? 'border-t-2 border-gray-400' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formattedDate}</td>
                      <td className={`px-4 py-3 text-sm ${isBold ? 'font-bold' : ''} text-gray-900`}>
                        {trans.particulars}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{trans.vchType || ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{trans.vchNo || ''}</td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${isBold ? 'font-bold' : ''} text-gray-900`}>
                        {trans.debit !== undefined ? formatCurrency(trans.debit) : ''}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${isBold ? 'font-bold' : ''} text-gray-900`}>
                        {trans.credit !== undefined ? formatCurrency(trans.credit) : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Accounts Table */}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ledger Name</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit Balance</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Balance</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {summary.accounts.map((acc, idx) => (
              <tr key={idx}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{acc.ledgerName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{formatCurrency(acc.debitBalance)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{formatCurrency(acc.creditBalance)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">Totals</td>
              <td className="px-6 py-3 text-sm font-semibold text-right text-gray-900">{formatCurrency(totalDebit)}</td>
              <td className="px-6 py-3 text-sm font-semibold text-right text-gray-900">{formatCurrency(totalCredit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default LedgerTable;


